import { makeDiscriminatedUnionPredicate } from '@sgme/fp';
import { ISearchResponse } from 'apiConnector/blotters/common';
import { apiConnector } from 'apiConnector/secondary-market-blotter';
import { isNavigatingInSecondaryTradeBlotter } from 'epic/utils/navigation/navigation';
import { IBlotterItem } from 'models/IBlotterItem';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { EMPTY, interval, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  map,
  merge,
  mergeMap,
  skipUntil,
  startWith,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import type { Action } from 'state/actions';
import { unhandledError } from 'state/actions';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import type { ISecondaryMarketTradeBlotterFilters } from 'state/blotters/secondaryMarketTradeBlotter';
import {
  initialSecondaryMarketTradeBlotterFilters,
  secondaryMarketTradeBlotterColumnDefinitionReceived,
  secondaryMarketTradeBlotterFiltersLoaded,
  secondaryMarketTradeBlotterItemsReceived,
  secondaryMarketTradeBlotterItemsReloaded,
  secondaryMarketTradeBlotterItemsRequested,
} from 'state/blotters/secondaryMarketTradeBlotter';
import type { ITradeRequest } from 'state/blotters/secondaryMarketTradeBlotter/secondaryMarketTradeBlotter.selector';
import type { EpicWithThunk } from 'state/state.common';

export interface IFetchColumnDefinitionEpicApi {
  fetchColumnDefinition(): Observable<IBlotterColumnDefinition[]>;
}

export function fetchColumnDefinitionEpic(): EpicWithThunk {
  return (actions$, _state$, { oldHttpClient }) =>
    actions$.pipe(
      filter(isNavigatingInSecondaryTradeBlotter),
      take(1),
      mergeMap(_ => {
        return apiConnector(oldHttpClient)
          .fetchColumnDefinition()
          .pipe(
            map(secondaryMarketTradeBlotterColumnDefinitionReceived),
            catchError(err =>
              of(
                unhandledError(
                  'Failed to fetch columns definitions for secondary market blotter',
                  err,
                  'fetchColumnDefinitionEpic',
                ),
              ),
            ),
          );
      }),
    );
}

export interface IFetchItemsEpicApi {
  search(request: ITradeRequest): Observable<ISearchResponse | null>;
}

const isFetchingAction = makeDiscriminatedUnionPredicate('type', [
  'SECONDARY_MARKET_TRADE_BLOTTER_FILTERS_LOADED',
  'SECONDARY_MARKET_TRADE_BLOTTER_PAGE_CHANGED',
  'SECONDARY_MARKET_TRADE_BLOTTER_ITEMS_PER_PAGE_CHANGED',
  'SECONDARY_MARKET_TRADE_BLOTTER_FILTER_CHANGED',
  'SECONDARY_MARKET_TRADE_BLOTTER_ITEMS_RELOADED',
] satisfies Action['type'][]);

export function fetchItemsEpic(aggregationFrequency: number = 500, scheduler?: SchedulerLike): EpicWithThunk {
  return (
    action$,
    state$,
    { oldHttpClient, frontLogger, selectors: { getSecondaryMarketTradeBlotterRequest }, getTodayString },
  ) => {
    const preferenceStateLoaded$ = action$.pipe(
      filter(({ type }) => type === 'SECONDARY_MARKET_TRADE_BLOTTER_PREFERENCE_STATE_LOADED'),
    );

    return action$.pipe(
      filter(isFetchingAction),
      skipUntil(action$.pipe(filter(({ type }) => type === 'SECONDARY_MARKET_TRADE_BLOTTER_PREFERENCE_STATE_LOADED'))),
      merge(preferenceStateLoaded$),
      debounceTime(aggregationFrequency, scheduler),
      withLatestFrom(state$),
      mergeMap(([, state]) => {
        const requestData = getSecondaryMarketTradeBlotterRequest(state);
        return apiConnector(oldHttpClient)
          .search(requestData, getTodayString())
          .pipe(
            map(res => {
              if (res === null) {
                const EMPTYResult: ISearchResponse<IBlotterItem> = {
                  entries: [],
                  pageInfo: {
                    size: 0,
                    totalElements: 0,
                    totalPages: 0,
                    page: 0,
                  },
                };
                return EMPTYResult;
              }
              return res;
            }),
            map(({ entries, pageInfo: { totalElements, totalPages } }) =>
              secondaryMarketTradeBlotterItemsReceived(entries, totalElements, totalPages),
            ),
            startWith(secondaryMarketTradeBlotterItemsRequested()),
            catchError(err => {
              frontLogger.error('secondary market trade-blotter failed to fetch items: {@FrontError}', err);
              return EMPTY;
            }),
          );
      }),
    );
  };
}

// TODO catch Error in Epic
export function persistFilters(): EpicWithThunk {
  return (action$, state$, { localStorageApiConnector, selectors: { getSecondaryMarketTradeBlotterFilters } }) =>
    action$.pipe(
      ofType(
        'SECONDARY_MARKET_TRADE_BLOTTER_FILTER_CHANGED',
        'SECONDARY_MARKET_TRADE_BLOTTER_ADVANCED_FILTERS_TOGGLED',
      ),
      withLatestFrom(state$),
      mergeMap(([, state]) => {
        const filters = getSecondaryMarketTradeBlotterFilters(state);
        localStorageApiConnector(
          'secondary-trade-blotter-filters',
          initialSecondaryMarketTradeBlotterFilters,
        ).save<ISecondaryMarketTradeBlotterFilters>(filters);
        return EMPTY;
      }),
    );
}

export function loadFilters(): EpicWithThunk {
  return (action$, _state$, { localStorageApiConnector }) =>
    action$.pipe(
      filter(isNavigatingInSecondaryTradeBlotter),
      take(1),
      mergeMap(_ => {
        return localStorageApiConnector('secondary-trade-blotter-filters', initialSecondaryMarketTradeBlotterFilters)
          .load<ISecondaryMarketTradeBlotterFilters>()
          .pipe(
            map(secondaryMarketTradeBlotterFiltersLoaded),
            catchError(err => of(unhandledError('Failed to load secondary market trade blotter filters', err))),
          );
      }),
    );
}

export function pollSecondaryMarketBlotterItemsEpic(scheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { selectors: { isInSecondaryMarketBlotterRoute } }) => {
    const navigationOut$: Observable<Action> = action$.pipe(filter(isNavigatingOut));

    return action$.pipe(
      filter(
        action =>
          action.type === 'SECONDARY_MARKET_TRADE_BLOTTER_ITEMS_RECEIVED' ||
          isNavigatingInSecondaryTradeBlotter(action),
      ),
      withLatestFrom(state$),
      filter(([, state]) => isInSecondaryMarketBlotterRoute(state)),
      switchMap(() => interval(10000, scheduler).pipe(takeUntil(navigationOut$))),
      map(() => secondaryMarketTradeBlotterItemsReloaded()),
    );
  };
}

function isNavigatingOut(action: Action) {
  return action.type === 'ROUTE_CHANGED' && action.path !== '/secondary-trade-blotter';
}
